import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient.ts';

const MARKING_FIELDS = 'marking_method, deterministic_config, ai_config';
// v2: bumped so any stale "disabled" value written before the marking
// migration was applied is ignored and the columns are requested again.
const MARKING_COLUMNS_STORAGE_KEY = 'huons.questions.markingColumnsEnabled.v2';

export const PRACTICE_SOLO_SELECT =
  'id, prompt, marks, category_id, type, choices, answer, answer_index, worked_solution, difficulty, group_id, unit';

export const PRACTICE_PART_SELECT =
  'id, prompt, marks, category_id, type, choices, answer, answer_index, worked_solution, difficulty, group_id, ordinal, unit';

export const TEST_QUESTION_FIELDS =
  'id, prompt, type, choices, answer, answer_index, marks, unit, worked_solution, group_id, ordinal, category_id';

export const REVIEW_QUESTION_SELECT =
  'id, prompt, type, choices, answer, answer_index, marks, unit, worked_solution';


export const isMissingColumnError = (error: PostgrestError | null | { message?: string; code?: string }) => {
  if (!error) return false;
  if (String((error as PostgrestError).code ?? '') === '42703') return true;
  const msg = `${error.message ?? ''} ${(error as PostgrestError).details ?? ''} ${(error as PostgrestError).hint ?? ''}`.toLowerCase();
  return msg.includes('column') && (msg.includes('does not exist') || msg.includes('schema cache'));
};

export const withMarkingFields = (baseSelect: string) => `${baseSelect}, ${MARKING_FIELDS}`;

type QueryResult<T> = { data: T | null; error: PostgrestError | null };

const shouldRequestMarkingColumns = () => {
  // Marking columns now exist in the database, so request them by default.
  // Allow an explicit opt-out via env var or a previously cached disable.
  if (process.env.REACT_APP_ENABLE_QUESTION_MARKING_COLUMNS === 'false') return false;
  if (typeof window !== 'undefined' && window.localStorage.getItem(MARKING_COLUMNS_STORAGE_KEY) === 'false') {
    return false;
  }
  return true;
};

const disableMarkingColumnRequests = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(MARKING_COLUMNS_STORAGE_KEY, 'false');
};

/**
 * Run a questions query with marking columns; fall back to legacy select if migrations
 * are not applied yet (questions.marking_method missing).
 */
export async function queryQuestionsWithMarkingFallback<T>(
  run: (select: string) => Promise<QueryResult<T>>,
  baseSelect: string,
): Promise<QueryResult<T> & { hasMarkingColumns: boolean }> {
  if (!shouldRequestMarkingColumns()) {
    const legacy = await run(baseSelect);
    return { ...legacy, hasMarkingColumns: false };
  }

  const withMarking = await run(withMarkingFields(baseSelect));
  if (!withMarking.error) {
    return { ...withMarking, hasMarkingColumns: true };
  }
  if (!isMissingColumnError(withMarking.error)) {
    return { ...withMarking, hasMarkingColumns: false };
  }
  disableMarkingColumnRequests();
  const legacy = await run(baseSelect);
  return { ...legacy, hasMarkingColumns: false };
}

/**
 * Questions (and groups that contain them) with an open flag. Practice hides
 * these so students don't keep hitting items already reported as broken.
 */
export async function fetchOpenFlaggedIds(): Promise<{
  questionIds: Set<string>;
  groupIds: Set<string>;
}> {
  const empty = { questionIds: new Set<string>(), groupIds: new Set<string>() };
  const { data, error } = await supabase
    .from('question_flags')
    .select('question_id')
    .eq('status', 'open');

  if (error) {
    console.error('Could not load open question flags:', error.message);
    return empty;
  }

  const questionIds = new Set(
    (data ?? []).map((row) => row.question_id as string).filter(Boolean),
  );
  if (questionIds.size === 0) return empty;

  const ids = Array.from(questionIds);
  const groupIds = new Set<string>();
  const CHUNK = 200;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const { data: qs, error: qErr } = await supabase
      .from('questions')
      .select('group_id')
      .in('id', ids.slice(i, i + CHUNK))
      .not('group_id', 'is', null);
    if (qErr) {
      console.error('Could not load flagged question groups:', qErr.message);
      break;
    }
    for (const q of qs ?? []) {
      if (q.group_id) groupIds.add(q.group_id as string);
    }
  }
  return { questionIds, groupIds };
}

export async function fetchTestQuestionRows(testId: string) {
  const buildSelect = (questionFields: string) => `
    ordinal,
    questions (
      ${questionFields},
      group:question_groups ( id, prompt )
    )
  `;

  if (shouldRequestMarkingColumns()) {
    const withMarking = await supabase
      .from('test_questions')
      .select(buildSelect(withMarkingFields(TEST_QUESTION_FIELDS)))
      .eq('test_id', testId)
      .order('ordinal');

    if (!withMarking.error) {
      return { ...withMarking, hasMarkingColumns: true };
    }
    if (!isMissingColumnError(withMarking.error)) {
      return { ...withMarking, hasMarkingColumns: false };
    }
    disableMarkingColumnRequests();
  }

  const legacy = await supabase
    .from('test_questions')
    .select(buildSelect(TEST_QUESTION_FIELDS))
    .eq('test_id', testId)
    .order('ordinal');

  return { ...legacy, hasMarkingColumns: false };
}
